import { EventsByResourceType, EventsListType, EventType, ResourceGroupType } from "../SchedulingTypes";
import { generateDayKey } from "./schedulingDateUtils";
import { MIN_EVENT_RENDER_MINUTES } from "../schedulingConstants";

// Get the rendered start and end time of an event in milliseconds, the end
// respects the minimum rendered duration so the visual overlap of widened
// tiny events is taken into account
export const getRenderedEventTimes = (event: EventType): { start: number, end: number } => {

    // Convert the start and end datetime to milliseconds
    const start = event.startDateTime ? new Date(event.startDateTime).getTime() : 0;
    const end = event.endDateTime ? new Date(event.endDateTime).getTime() : start;

    // Widen the end time to the minimum rendered duration
    return { start, end: Math.max(end, start + MIN_EVENT_RENDER_MINUTES * 60 * 1000) };
};

// Assign a stack lane to each event, events which do not visually overlap
// share the same lane so they stay on one row within the resource row
export const calculateEventLanes = (events: EventType[]): Map<string, number> => {
    const eventLanes = new Map<string, number>();
    const laneEndTimes: number[] = [];

    // Sort the events on rendered start time with the key as tie breaker
    const sortedEvents = [...events]
        .filter(event => event.startDateTime && event.endDateTime && !event.deleted)
        .sort((eventA, eventB) => {
            const startDifference = getRenderedEventTimes(eventA).start - getRenderedEventTimes(eventB).start;
            if (startDifference !== 0) return startDifference;
            return eventA.key.localeCompare(eventB.key);
        });

    // Place every event on the first lane which is free at its start time
    sortedEvents.forEach(event => {
        const { start, end } = getRenderedEventTimes(event);

        // Find the first free lane or open a new lane
        let lane = laneEndTimes.findIndex(laneEndTime => laneEndTime <= start);
        if (lane === -1) lane = laneEndTimes.length;

        // Occupy the lane until the rendered end of the event
        laneEndTimes[lane] = end;
        eventLanes.set(event.key, lane);
    });

    return eventLanes;
};

// Count the number of lanes needed to stack the given events
export const calculateLaneCount = (events: EventType[]): number => {

    // Assign the lanes and return the highest lane number plus one
    const eventLanes = calculateEventLanes(events);
    let laneCount = 0;
    eventLanes.forEach(lane => { laneCount = Math.max(laneCount, lane + 1); });
    return laneCount;
};

// Calculate overlapping events
export const calculateOverlappingEvents = (
    resourceGroups: ResourceGroupType[], 
    days: Date[], 
    events: EventsListType, 
    eventsByResource: EventsByResourceType
) => {
    const newEventOverlapMap = new Map<string, EventType[]>();

    // Loop through the resource groups
    resourceGroups.forEach(group => {

        // Loop through the resources of each group
        group.resources.forEach(resource => {
            if (!resource.id) return;

            // Loop through the day of each resource
            days.forEach(day => {

                // Generate a day key
                const dayKey = generateDayKey(day)

                // Get all events for this resource and day
                const resourceEvents = (eventsByResource[resource.id!] || [])
                    .map(eventId => events[eventId])
                    .filter(event => event !== undefined && !event.deleted);

                // Sort the events on start time
                resourceEvents.sort((a, b) => {
                    const timeA = a.startDateTime ? new Date(a.startDateTime).getTime() : 0;
                    const timeB = b.startDateTime ? new Date(b.startDateTime).getTime() : 0;

                    if (timeA !== timeB) return timeA - timeB;

                    // Compare on the key of the event to prevent reordering of overlapping events
                    return a.key.localeCompare(b.key);
                });

                // Add the events to the map
                newEventOverlapMap.set(`${resource.id}-${dayKey}`, resourceEvents);
            });
        });
    });

    return newEventOverlapMap;
};

// Get the amount of overlapping events and the event width of a certain timeslot
export const getOverlappingEvents = (
    eventOverlapMap: Map<string, EventType[]>,
    resourceId: string, 
    dayKey: string, 
    timeSlot: string
): [EventType[], number] => {
    const allEvents = eventOverlapMap.get(`${resourceId}-${dayKey}`) || [];
    const slotStartTime = new Date(`${dayKey}T${timeSlot}:00`).getTime();
    const slotEndTime = slotStartTime + 30 * 60 * 1000;

    // Filter events to find overlaps with the given day and time
    let overlappingEvents = allEvents.filter(event => {
        if (!event.startDateTime || !event.endDateTime || event.deleted) return false;

        const { start: eventStart, end: eventEnd } = getRenderedEventTimes(event);

        // Check if the event is in the given day
        const eventStartDay = event.startDateTime.split("T")[0];
        const eventEndDay = event.endDateTime.split("T")[0];

        const isSameDay = eventStartDay === dayKey;
        const overlapsIntoThisDay = eventStartDay < dayKey && eventEndDay >= dayKey;

        // Check if the event overlaps with the given timeslot
        const overlapsInTime =
            // Event starts before the end of the timeslot
            eventStart < slotEndTime && // Event begint vóór het einde van het tijdslot

            // Event ends after the start of the timeslot
            eventEnd > slotStartTime;

        return (isSameDay || overlapsIntoThisDay) && overlapsInTime;
    });

    // Find events which overlap with the found events
    let extraEventsFound;
    do {
        extraEventsFound = false;

        const newOverlappingEvents = allEvents.filter(event => {
            if (!event.startDateTime || !event.endDateTime || event.deleted) return false;

            // Prevent duplicated events
            if (overlappingEvents.includes(event)) return false;

            const { start: eventStart, end: eventEnd } = getRenderedEventTimes(event);

            return overlappingEvents.some(overlappingEvent => {
                const { start: overlappingStart, end: overlappingEnd } = getRenderedEventTimes(overlappingEvent);

                const isOverlapping =
                    eventStart < overlappingEnd && eventEnd > overlappingStart;

                if (isOverlapping) extraEventsFound = true;
                return isOverlapping;
            });
        });

        if (extraEventsFound) {
            overlappingEvents = [...overlappingEvents, ...newOverlappingEvents];
        }
    } while (extraEventsFound);

    // Calculate the width percentage of the events for this timeslot, based
    // on the number of lanes needed to stack the events without visual overlap
    const laneCount = calculateLaneCount(overlappingEvents);
    const widthPercentage = laneCount > 0 ? 100 / laneCount : 100

    return [overlappingEvents, widthPercentage];
};